// Pure helpers for the Scenes tab: normalize the /scenes payload and derive lanes, filters and detail cards.

/** cache the last result of fn for identical (===) arguments */
export function memoize(fn) {
  let last = null,
    result;
  return (...args) => {
    if (last && last.length === args.length && last.every((v, i) => v === args[i])) return result;
    last = args;
    result = fn(...args);
    return result;
  };
}

const p2 = (n) => String(n).padStart(2, "0");

export function fmt(s) {
  s = Math.max(0, Number(s) || 0);
  const h = Math.floor(s / 3600),
    m = Math.floor(s / 60) % 60,
    x = Math.floor(s % 60);
  return h > 0 ? h + ":" + p2(m) + ":" + p2(x) : m + ":" + p2(x);
}

export const rangeLabel = (a, b) => fmt(a) + "-" + fmt(b);

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

export const TAGS = {
  alcohol: "Alcohol",
  blood_or_injury: "Blood or injury",
  death: "Death",
  drug_use: "Drug use",
  self_harm: "Self-harm",
  sexual_content: "Sexual content",
  smoking: "Smoking",
  strong_language: "Strong language",
  violence: "Violence",
  weapon: "Weapon",
};
export const tagLabel = (t) => TAGS[t] || cap(String(t).replace(/_/g, " "));

export const ROLE = { central: "Central", involved: "Involved", background: "Background" };
export const ARC_ROLE = { setup: "Setup", development: "Development", "turning point": "Turning point", climax: "Climax", resolution: "Resolution" };
export const ARC_STATUS = { cliffhanger: "Cliffhanger at episode end", resolved: "Resolved at episode end", ongoing: "Ongoing at episode end" };

// arc step markers on the story map; the turning point's 8px square turned 45° spans about 11px
export const GLYPH = {
  setup: { size: 10, bg: "var(--p-surface)", border: "2px solid var(--p-accent)", radius: "50%", rotate: "0deg" },
  development: { size: 6, bg: "var(--p-accent)", border: "0", radius: "50%", rotate: "0deg" },
  "turning point": { size: 8, bg: "var(--p-ink)", border: "0", radius: "1px", rotate: "45deg" },
  climax: { size: 10, bg: "var(--p-ink)", border: "0", radius: "50%", rotate: "0deg" },
  resolution: { size: 10, bg: "var(--p-surface)", border: "2px solid var(--p-ink)", radius: "1px", rotate: "0deg" },
};
export const glyph = (role) => GLYPH[role] || GLYPH.development;

export const WEIGHT = { A: ["var(--p-wA)", "var(--p-wAf)"], B: ["var(--p-wB)", "var(--p-wBf)"], C: ["var(--p-wC)", "var(--p-wCf)"] };
export const weightColors = (w) => WEIGHT[w] || WEIGHT.C;

/**
 * a letter of its own for each arc, main plot first (A, B, C… like an episode's A-plot, B-plot): the weight (A main,
 * B secondary, C minor) is shared by several arcs, so it can't tell them apart. The weight shows in the badge color.
 */
export function arcLabels(arcs) {
  const rank = { A: 0, B: 1, C: 2 };
  const order = (arcs || []).map((a, n) => n).sort((i, j) => (rank[arcs[i].weight] ?? 2) - (rank[arcs[j].weight] ?? 2) || i - j);
  const labels = [];
  order.forEach((n, pos) => (labels[n] = pos < 26 ? String.fromCharCode(65 + pos) : String(pos + 1)));
  return labels;
}

export const CAST_COLOR = { central: "var(--p-accent)", involved: "var(--p-accline)", background: "var(--p-tint2)" };
const CAST_WEIGHT = { central: 2, involved: 1, background: 0.5 };

const firstSentence = (s) => {
  const m = String(s || "").match(/^.*?[.!?](\s|$)/);
  return (m ? m[0] : String(s || "")).trim();
};

/**
 * Normalize the /scenes payload into the model the tab renders.
 * Character labels are mapped to the project's current names through their char ids,
 * so renames from the Tracks tab show up here too.
 * @param data {scenes, arcs} from GET /video_project/:id/scenes
 * @param sentences redux sentences.items
 * @param dic redux characters.dic (char_id -> name)
 */
export function buildModel(data, sentences, dic) {
  const raw = (data && data.scenes) || [];
  dic = dic || {};

  const names = {};
  const learn = (label, id) => {
    if (label && id !== null && id !== undefined && dic[id]) names[label] = dic[id];
  };
  raw.forEach((x) => {
    (x.characters || []).forEach((c) => learn(c.name, c.id));
    (x.moments || []).concat(x.beats || []).forEach((m) => (m.who || []).forEach((w, k) => learn(w, (m.who_ids || [])[k])));
    (x.facts || []).forEach((f) => {
      learn(f.subject, f.subject_id);
      learn(f.object, f.object_id);
    });
  });
  const rn = (n) => names[n] || n;
  const segment = (m) => ({ s: m.start_time, e: m.end_time, text: m.text || "", who: (m.who || []).map(rn) });

  const scenes = raw.map((x, i) => {
    // scenes analyzed before summary existed only have the longer description
    const summary = x.summary || x.description || "";
    return {
      i,
      index: x.index,
      s: x.start_time,
      e: x.end_time,
      shots: x.shots || [],
      isTransition: x.scene_type === "transition" || (!x.headline && !!x.transition),
      title: x.headline || x.transition || firstSentence(summary),
      summary,
      setting: x.setting || "",
      outcome: x.outcome || "",
      tags: x.tags || [],
      characters: (x.characters || []).map((c) => ({ name: rn(c.name), role: c.role, id: c.id })),
      others: x.others || [],
      moments: (x.moments || []).map(segment),
      beats: (x.beats || []).map(segment),
      facts: (x.facts || []).map((f) => ({ kind: (f.kind || "fact").replace(/_/g, " "), text: f.text || "", subject: rn(f.subject), object: rn(f.object), evidence: f.evidence || "" })),
      lines: [],
      arcs: [],
    };
  });

  // transcript lines come from the project sentences, binned by start time
  const sorted = (sentences || []).slice().sort((a, b) => a.start_time - b.start_time);
  let k = 0;
  sorted.forEach((l) => {
    while (k < scenes.length && l.start_time >= scenes[k].e) k++;
    if (k < scenes.length && l.start_time >= scenes[k].s) scenes[k].lines.push({ t: l.start_time, speaker: dic[l.char_id] || "", text: l.text || "" });
  });

  const pos = {};
  scenes.forEach((x) => (pos[x.index] = x.i));
  const arcs = ((data && data.arcs) || []).map((a, n) => {
    const steps = (a.scenes || []).filter((q) => pos[q.scene_index] !== undefined).map((q) => ({ i: pos[q.scene_index], role: q.role, note: q.note || "" }));
    steps.forEach((q) => scenes[q.i].arcs.push({ k: n, role: q.role, note: q.note }));
    return { k: n, title: a.title || "", weight: a.weight || "C", status: a.status || "", summary: a.summary || "", characters: (a.characters || []).map(rn), steps };
  });
  arcLabels(arcs).forEach((label, n) => (arcs[n].label = label));

  scenes.forEach((x) => {
    x.text = [x.title, x.summary, x.setting, x.outcome, x.characters.map((c) => c.name).join(" "), x.moments.map((m) => m.text).join(" "), x.facts.map((f) => f.text).join(" ")]
      .join(" ")
      .toLowerCase();
  });

  const castNames = new Set();
  scenes.forEach((x) => x.characters.forEach((c) => castNames.add(c.name)));
  const duration = scenes.reduce((d, x) => Math.max(d, x.e), 0) || 1;
  const tags = [...new Set(scenes.flatMap((x) => x.tags))];

  return { scenes, arcs, characterNames: [...castNames], tags, duration };
}

export const sceneAt = (model, t) => model.scenes.findIndex((x) => t >= x.s && t < x.e);

/** ms of the scene's thumbnail frame: just after the cut so it isn't the previous shot; rounded so it caches well */
export const thumbMs = (x) => Math.round((x.s + Math.min(0.5, (x.e - x.s) / 2)) * 10) * 100;

/** filters: {tag, q (lowercase), arc (index), who (name)} */
export function matchScene(model, x, f) {
  if (f.tag && !x.tags.includes(f.tag)) return false;
  if (f.q && !x.text.includes(f.q) && !x.lines.some((l) => (l.speaker + " " + l.text).toLowerCase().includes(f.q))) return false;
  if (f.arc !== null && f.arc !== undefined && !x.arcs.some((a) => a.k === f.arc)) return false;
  if (f.who && !x.characters.some((c) => c.name === f.who)) return false;
  return true;
}

export const hasFilter = (f) => !!(f.tag || f.q || (f.arc !== null && f.arc !== undefined) || f.who);

const countIn = (v, q) => (q ? String(v || "").toLowerCase().split(q).length - 1 : 0);

export function countMatches(x, q) {
  if (!q) return 0;
  return [x.title, x.summary, x.setting, x.outcome]
    .concat(
      x.characters.map((c) => c.name),
      x.moments.map((m) => m.text),
      x.beats.map((m) => m.text),
      x.facts.map((m) => m.text),
      x.lines.map((l) => l.speaker + " " + l.text)
    )
    .reduce((n, v) => n + countIn(v, q), 0);
}

export const countLineMatches = (x, q) => x.lines.reduce((n, l) => n + countIn(l.speaker + " " + l.text, q), 0);

/** split text into [{t, hit}] segments around case-insensitive matches of q */
export function highlight(text, q) {
  const s = text === null || text === undefined ? "" : String(text);
  if (!q) return [{ t: s, hit: false }];
  const low = s.toLowerCase(),
    out = [];
  let k = 0,
    j;
  while ((j = low.indexOf(q, k)) >= 0) {
    if (j > k) out.push({ t: s.slice(k, j), hit: false });
    out.push({ t: s.slice(j, j + q.length), hit: true });
    k = j + q.length;
  }
  if (k < s.length) out.push({ t: s.slice(k), hit: false });
  return out.length ? out : [{ t: "", hit: false }];
}

const TICK_STEPS = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

/** ruler ticks for the window [v0, v0 + span]: at most 9; f is the position within the window (0-1) */
export function ticks(span, v0 = 0) {
  const step = TICK_STEPS.find((s) => span / s <= 9) || 3600;
  const out = [];
  for (let t = Math.ceil(v0 / step) * step; t < v0 + span - step * 0.3; t += step) {
    const f = (t - v0) / span;
    out.push({ t, label: fmt(t), f, left: (f * 100).toFixed(3) + "%" });
  }
  return out;
}

export const pct = (t, duration) => ((t / duration) * 100).toFixed(3) + "%";

/** deepest story map zoom: 64x, and the window never gets shorter than a minute */
export const maxZoom = (duration) => Math.max(1, Math.min(64, duration / 60));

/** story map window: zoom z (1 = whole episode) showing [v0, v0 + span], clamped to the episode */
export function viewOf(duration, z, v0) {
  z = Math.max(1, Math.min(z || 1, maxZoom(duration)));
  const span = duration / z;
  return { duration, z, span, v0: Math.max(0, Math.min(v0 || 0, duration - span)) };
}

export const isZoomed = (view) => view.z > 1.001;

/** position of time t / width of [a, b] in the window, as css percentages */
export const viewPct = (t, view) => (((t - view.v0) / view.span) * 100).toFixed(3) + "%";
export const viewWidth = (a, b, view) => (((b - a) / view.span) * 100).toFixed(3) + "%";

/**
 * window zoomed to z: keeps time anchorT at fraction anchorF of the window (mouse zoom),
 * else centers on the focus scene when it is visible, else keeps the window center
 */
export function zoomView(view, z, anchorT, anchorF, focus) {
  const span = view.duration / Math.max(1, Math.min(z, maxZoom(view.duration)));
  let v0;
  if (anchorT !== undefined) v0 = anchorT - anchorF * span;
  else {
    const m = focus ? mid(focus) : -1;
    v0 = (m >= view.v0 && m <= view.v0 + view.span ? m : view.v0 + view.span / 2) - span / 2;
  }
  return viewOf(view.duration, z, v0);
}

/** the same window moved so scene x is in view (centered), or null when it already is */
export function revealView(view, x) {
  if (!isZoomed(view) || (x.e >= view.v0 && x.s <= view.v0 + view.span)) return null;
  return viewOf(view.duration, view.z, mid(x) - view.span / 2);
}

const mid = (x) => (x.s + x.e) / 2;

export function arcLanes(model) {
  return model.arcs.map((a) => {
    const idx = a.steps.map((q) => q.i);
    const lo = idx.length ? Math.min(...idx) : 0,
      hi = idx.length ? Math.max(...idx) : 0;
    const S = model.scenes;
    return {
      k: a.k,
      title: a.title,
      weight: a.weight,
      label: a.label,
      scenes: new Set(idx),
      spanL: S[lo] ? mid(S[lo]) : 0,
      spanR: S[hi] ? mid(S[hi]) : 0,
      // still open when the episode ends (cliffhanger, ongoing)
      continues: !!a.status && a.status !== "resolved",
      marks: a.steps.map((q) => ({ i: q.i, role: q.role, note: q.note, t: mid(S[q.i]), s: S[q.i].s, e: S[q.i].e })),
    };
  });
}

export function characterLanes(model) {
  const wt = {},
    cells = {},
    ids = {};
  model.scenes.forEach((x) =>
    x.characters.forEach((c) => {
      wt[c.name] = (wt[c.name] || 0) + (CAST_WEIGHT[c.role] || CAST_WEIGHT.background);
      (cells[c.name] = cells[c.name] || []).push({ i: x.i, role: c.role });
      if (ids[c.name] === undefined && c.id !== null && c.id !== undefined) ids[c.name] = c.id;
    })
  );
  return Object.keys(wt)
    .sort((a, b) => wt[b] - wt[a])
    .map((name) => ({ name, id: ids[name] === undefined ? null : ids[name], cells: cells[name] }));
}

export function arcDetail(model, k) {
  const a = model.arcs[k];
  if (!a) return null;
  return {
    ...a,
    steps: a.steps.map((q) => {
      const x = model.scenes[q.i];
      return { ...q, t: x.s, headline: x.isTransition ? "" : x.title };
    }),
  };
}

const evidenceTime = (evidence, fallback) => {
  const m = String(evidence || "").match(/\[(\d+(?:\.\d+)?)\]/);
  return m ? parseFloat(m[1]) : fallback;
};

/** time covered by tracks [{start_time, end_time}]; overlapping tracks (e.g. face + body) count once */
export function screenTime(tracks) {
  const iv = (tracks || [])
    .map((t) => [t.start_time, t.end_time])
    .filter(([a, b]) => b > a)
    .sort((a, b) => a[0] - b[0]);
  let total = 0,
    start = null,
    end = null;
  iv.forEach(([a, b]) => {
    if (end === null || a > end) {
      if (end !== null) total += end - start;
      start = a;
      end = b;
    } else if (b > end) end = b;
  });
  if (end !== null) total += end - start;
  return total;
}

/**
 * seconds on screen (the character's tracks) and speaking (their transcript lines) per char_id
 * @param items redux characters.items
 * @param sentences redux sentences.items
 */
export function castTimes(items, sentences) {
  const m = {};
  const of = (id) => m[id] || (m[id] = { screen: 0, speech: 0 });
  (items || []).forEach((c) => (of(c.char_id).screen = screenTime(c.tracks_data)));
  (sentences || []).forEach((l) => {
    if (l.char_id !== null && l.char_id !== undefined) of(l.char_id).speech += Math.max(0, l.end_time - l.start_time);
  });
  return m;
}

export function characterDetail(model, name) {
  let present = 0,
    central = 0,
    id = null;
  const facts = [],
    beats = [],
    steps = [];
  model.scenes.forEach((x) => {
    const c = x.characters.find((c) => c.name === name);
    if (c) {
      present++;
      if (c.role === "central") central++;
      if (id === null && c.id !== null && c.id !== undefined) id = c.id;
    }
    x.moments.forEach((m) => m.who.includes(name) && steps.push({ i: x.i, t: m.s, text: m.text }));
    x.beats.forEach((m) => m.who.includes(name) && beats.push({ i: x.i, t: m.s, text: m.text }));
    x.facts.forEach((f) => (f.subject === name || f.object === name) && facts.push({ i: x.i, t: evidenceTime(f.evidence, x.s), kind: f.kind, text: f.text }));
  });
  return { name, id, present, central, facts, beats, steps };
}
